import { FC, useState } from 'react'
import { Box, Collapse } from '@mui/material'
import theme from 'theme'
import {
  ArrowTrendingUpIcon,
  ChartBarIcon,
  ComputerDesktopIcon,
  PlusCircleIcon,
  UserIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import { AssetInventoryType, AssetRow, IntegrationInstance, LastSeenWindow } from 'models'
import ChevronRight from 'ui/components/svg/ChevronRight'
import { StatisticsGrid, StatisticsToggle } from '../assetInventory.styles'
import { AssetTabConfig } from '../assetInventory.constants'
import { useAssetColumns } from '../hooks/useAssetColumns'
import { useAssetTabData } from '../hooks/useAssetTabData'
import EntityCountCard from '../components/EntityCountCard'
import RecentlyAddedCard from '../components/RecentlyAddedCard'
import EntitiesOverTimeCard from '../components/EntitiesOverTimeCard'
import EntitiesBreakdownCard from '../components/EntitiesBreakdownCard'
import AssetsGrid from '../components/AssetsGrid'
import AssetDetailsPane from '../components/AssetDetailsPane'

interface Props {
  config: AssetTabConfig
  instances: IntegrationInstance[]
  lastSeen: LastSeenWindow
  active: boolean
}

const AssetTab: FC<Props> = ({ config, instances, lastSeen, active }) => {
  const data = useAssetTabData({ config, lastSeen, active })
  const [selectedRow, setSelectedRow] = useState<AssetRow | null>(null)
  /* Opens expanded; scrolling down the grid folds it away so the rows get the height. */
  const [statisticsOpen, setStatisticsOpen] = useState(true)
  const { columns, status: columnsStatus } = useAssetColumns(config.assetType)
  const isIdentities = config.assetType === AssetInventoryType.Identities
  const entityIcon = isIdentities ? <UsersIcon /> : <ComputerDesktopIcon />
  /* The design draws one person on the count card and two on the grid header. */
  const countIcon = isIdentities ? <UserIcon /> : <ComputerDesktopIcon />

  return (
    <>
      {/* One flex item, so a collapsed section leaves no empty gap in the panel. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <StatisticsToggle
          type="button"
          aria-expanded={statisticsOpen}
          onClick={() => setStatisticsOpen((open) => !open)}
        >
          <Box component="span" className="toggle-chevron">
            <ChevronRight />
          </Box>
          {statisticsOpen ? 'Hide statistics' : 'Show statistics'}
        </StatisticsToggle>

        <Collapse in={statisticsOpen}>
          <StatisticsGrid sx={{ marginTop: `${theme.spacing.small}px` }}>
            <Box className="number-cards-cell">
              <EntityCountCard
                title={`Number of ${config.entityNounPlural}`}
                icon={countIcon}
                count={data.count}
                loading={data.statsLoading}
              />
              <RecentlyAddedCard
                icon={<PlusCircleIcon />}
                recentlyAdded={data.recentlyAdded}
                loading={data.statsLoading}
                onApplyFilter={data.applyFilter}
              />
            </Box>

            <EntitiesOverTimeCard
              title={`Number of ${config.entityNounPlural} over time`}
              icon={<ArrowTrendingUpIcon />}
              unit={config.entityNounPlural}
              points={data.trend}
              loading={data.trendLoading}
              period={data.trendPeriod}
              onPeriodChange={data.setTrendPeriod}
            />

            <Box className="breakdown-cell">
              <EntitiesBreakdownCard
                title={`${config.entityNounPlural} Breakdown`}
                icon={<ChartBarIcon />}
                unit={config.entityNounPlural}
                breakdown={data.breakdown}
                loading={data.breakdownLoading}
                dimensions={config.breakdownDimensions}
                dimension={data.dimension}
                onDimensionChange={data.setDimension}
                onSelectBucket={(bucket) => data.applyFilter(bucket.filter)}
              />
            </Box>
          </StatisticsGrid>
        </Collapse>
      </Box>

      <AssetsGrid
        title={config.entityNounPlural}
        icon={entityIcon}
        assetType={config.assetType}
        assetColumns={columns}
        columnsStatus={columnsStatus}
        lastSeen={lastSeen}
        externalFilters={data.filters}
        onClearFilter={data.clearFilter}
        onRowClick={setSelectedRow}
        onScrollDown={() => setStatisticsOpen(false)}
      />

      <AssetDetailsPane
        title={config.detailsTitle}
        assetType={config.assetType}
        columns={columns}
        instances={instances}
        row={selectedRow}
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
      />
    </>
  )
}

export default AssetTab
